// Pure helpers shared by the manual deliveries list: money, the pickup window text in a price confirm, and booking
// a row only at a price staff confirmed.

export function formatCents(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

const dateTimeOptions = {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
};

// A UTC instant as "Oct 9, 12:30 PM" in the given IANA zone (the shipment's, not the browser's).
export function formatInZone(iso, timeZone) {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    ...dateTimeOptions,
    timeZone,
  }).format(date);
}

function timeOnlyInZone(iso, timeZone) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(iso));
}

function localDay(iso, timeZone) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).format(new Date(iso));
}

// "Oct 9, 12:30 PM and 2:30 PM"; both dates when the range crosses local midnight.
function formatRangeInZone(startIso, endIso, timeZone) {
  const start = formatInZone(startIso, timeZone);
  const end = formatInZone(endIso, timeZone);
  if (!start || !end) return null;
  const sameDay = localDay(startIso, timeZone) === localDay(endIso, timeZone);
  return `${start} and ${sameDay ? timeOnlyInZone(endIso, timeZone) : end}`;
}

// The pickup line, or null when the quote has no pickup time. Only `direct` has a known end; a fixed city
// window service shows the start and warns that Trexity refuses a start it does not offer. Accepts quote
// details or a stored quote's raw (same keys).
export function pickupWindowText(details) {
  if (!details || !details.pickup_start_at) return null;
  const zone = details.time_zone;
  const start = formatInZone(details.pickup_start_at, zone);
  if (!start) return null;
  if (details.pickup_window_kind === "fixed_city_window") {
    return `Trexity pickup starts at ${start} (${zone}). This service only accepts the start of one of Trexity's fixed city pickup windows; if this time is not one of them, Trexity will refuse the booking.`;
  }
  const range = formatRangeInZone(
    details.pickup_start_at,
    details.pickup_end_at,
    zone
  );
  return range
    ? `We will request pickup between ${range} (${zone})`
    : `We will request pickup from ${start} (${zone})`;
}

// The carrier price stored on a list row, or null. unconfirmed is true when the server re-priced the row
// (a 409) and staff have not accepted that price yet.
export function storedQuotePrice(delivery) {
  const quote = (delivery && delivery.quote) || {};
  if (!Number.isInteger(quote.price_cents)) return null;
  return {
    priceCents: quote.price_cents,
    unconfirmed: quote.unconfirmed === true,
  };
}

// Schedules a pending carrier row only at a price staff accepted in a confirm dialog. retry(priceCents) posts
// the schedule with confirmed_price_cents (null when no price is known yet, which the server answers with a
// 409 and the fresh price); confirm(message, priceCents) returns a boolean or a Promise of one (the page's
// in-page alertdialog), and anything but true books nothing. A 409 shows the new price and re-posts only after a second explicit yes, so the booked price
// is always one staff saw.
// -> { cancelled: true, result? } or { cancelled: false, result }
export async function scheduleAtConfirmedPrice(
  storedPriceCents,
  retry,
  confirm,
  storedDetails = null
) {
  let price = Number.isInteger(storedPriceCents) ? storedPriceCents : null;
  const storedPickup = pickupWindowText(storedDetails);
  if (
    price !== null &&
    (await confirm(
      `Book this delivery with the carrier for ${formatCents(price)}?${
        storedPickup ? ` ${storedPickup}` : ""
      }`,
      price
    )) !== true
  ) {
    return { cancelled: true };
  }
  let result = await retry(price);
  const fresh = result.status === 409 && result.data && result.data.quote;
  if (fresh && Number.isInteger(fresh.price_cents)) {
    price = fresh.price_cents;
    const pickup = pickupWindowText(fresh.details);
    if (
      (await confirm(
        `The carrier price is now ${formatCents(price)}. ${
          pickup ? `${pickup} ` : ""
        }Book at this price?`,
        price
      )) !== true
    ) {
      return { cancelled: true, result };
    }
    result = await retry(price);
  }
  return { cancelled: false, result };
}
